import { useState, useCallback, useMemo } from 'react';
import { deriveViewModeFromLockInfo } from './editLockUtils';
import { DEFAULT_LOCK_MESSAGES, LockMessages, LockComponentType, Action } from './constants';
import { EntityLockedError } from 'src/models/common/EntityLockedError';
import { LockInfo } from 'src/models/common/LockInfo';
import apiService from 'src/services/ApiCallService';
import TPEAction from 'src/models/common/TPEAction';
import CONSTANTS from 'src/utils/constants';

interface UseLockActionsParams {
    componentType: LockComponentType;
    entityId: string | undefined;
    currentUser: string;
    dispatch: React.Dispatch<TPEAction>;
    setViewModeAction: TPEAction;
    messageService: any;
    onRefetch: () => void;
    messages?: Partial<LockMessages>;
}

export interface LockActionsResult {
    handleEdit: () => void;
    handleSave: () => void;
    handleConfirm: () => void;
    handleRevert: () => void;
    actionInProgress: Action | null;
}

export function useLockActions({
    componentType,
    entityId,
    currentUser,
    dispatch,
    setViewModeAction,
    messageService,
    onRefetch,
    messages: customMessages,
}: UseLockActionsParams): LockActionsResult {
    const [actionInProgress, setActionInProgress] = useState<Action | null>(null);
    const messages = useMemo(() => ({ ...DEFAULT_LOCK_MESSAGES, ...customMessages }), [customMessages]);

    const handleEdit = useCallback(async () => {
        if (!entityId) return;
        setActionInProgress(Action.EDIT);
        try {
            const response = await apiService.editEntityDraft(componentType, entityId);
            const json = await response.json();
            const lockInfo: LockInfo = json?.lockInfo;
            dispatch(setViewModeAction.withPayload(deriveViewModeFromLockInfo(lockInfo, currentUser)));
            messageService.showSuccessBanner(messages.editSuccess);
        } catch (ex) {
            if (ex instanceof EntityLockedError) {
                dispatch(setViewModeAction.withPayload(CONSTANTS.VIEW_MODE.READ_ONLY));
                messageService.showWarningBanner(messages.lockedByOther(ex.lockedBy || 'another user'));
                onRefetch();
            } else {
                messageService.showErrorBanner(`${messages.editFailure}: ${ex instanceof Error ? ex.message : String(ex)}`);
            }
        } finally {
            setActionInProgress(null);
        }
    }, [componentType, entityId, currentUser, dispatch, setViewModeAction, messageService, onRefetch, messages]);

    const handleSave = useCallback(async () => {
        if (!entityId) return;
        setActionInProgress(Action.SAVE);
        dispatch(setViewModeAction.withPayload(CONSTANTS.VIEW_MODE.READ_ONLY));
        try {
            await apiService.saveEntityDraftEdit(componentType, entityId);
            dispatch(setViewModeAction.withPayload(CONSTANTS.VIEW_MODE.EDITABLE));
            messageService.showSuccessBanner(messages.saveSuccess);
        } catch (ex) {
            dispatch(setViewModeAction.withPayload(CONSTANTS.VIEW_MODE.EDITABLE));
            messageService.showErrorBanner(`${messages.saveFailure}: ${ex instanceof Error ? ex.message : String(ex)}`);
        } finally {
            setActionInProgress(null);
        }
    }, [componentType, entityId, dispatch, setViewModeAction, messageService, messages]);

    const handleConfirm = useCallback(async () => {
        if (!entityId) return;
        setActionInProgress(Action.CONFIRM);
        dispatch(setViewModeAction.withPayload(CONSTANTS.VIEW_MODE.READ_ONLY));
        try {
            await apiService.confirmEntityDraftEdit(componentType, entityId);
            messageService.showSuccessBanner(messages.confirmSuccess);
            onRefetch();
        } catch (ex) {
            dispatch(setViewModeAction.withPayload(CONSTANTS.VIEW_MODE.EDITABLE));
            messageService.showErrorBanner(`${messages.confirmFailure}: ${ex instanceof Error ? ex.message : String(ex)}`);
        } finally {
            setActionInProgress(null);
        }
    }, [componentType, entityId, dispatch, setViewModeAction, messageService, onRefetch, messages]);

    const handleRevert = useCallback(async () => {
        if (!entityId) return;
        setActionInProgress(Action.REVERT);
        dispatch(setViewModeAction.withPayload(CONSTANTS.VIEW_MODE.READ_ONLY));
        try {
            await apiService.revertEntityDraftEdit(componentType, entityId);
            messageService.showSuccessBanner(messages.revertSuccess);
            onRefetch();
        } catch (ex) {
            dispatch(setViewModeAction.withPayload(CONSTANTS.VIEW_MODE.EDITABLE));
            messageService.showErrorBanner(`${messages.revertFailure}: ${ex instanceof Error ? ex.message : String(ex)}`);
        } finally {
            setActionInProgress(null);
        }
    }, [componentType, entityId, dispatch, setViewModeAction, messageService, onRefetch, messages]);

    return { handleEdit, handleSave, handleConfirm, handleRevert, actionInProgress };
}
